import { api } from "./api";
import type {
  TimeEntry,
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
  WeekTimeEntries,
  Project,
  TaskType,
  Client,
  UserTimeReport,
  WeeklyReport,
  ProjectRole,
  CostType,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateClientRequest,
  UpdateClientRequest,
  CreateTaskTypeRequest,
  UpdateTaskTypeRequest,
  CreateProjectRoleRequest,
  UpdateProjectRoleRequest,
} from "@/types";

export interface OwnProjectBalance {
  projectId: number;
  estimatedHours: number | null;
  workedHoursToDate: number;
  hoursBalance: number | null;
}

export interface ProjectDetailsReport {
  id: number;
  projectNumber: string;
  projectName: string;
  clientName: string;
  startDate: string | null;
  endDate: string | null;
  totalPlannedHours: number;
  totalWorkedHours: number;
  totalHourBalance: number;
  members: {
    key: string;
    employeeId: number | null;
    employeeName: string;
    role: string;
    isAssigned: boolean;
    plannedHours: number | null;
    workedHours: number;
    hourBalance: number | null;
  }[];
}

export const timesheetService = {
  getActiveProjectDetails() {
    return api.get<ProjectDetailsReport[]>("/api/Reports/active-project-details");
  },

  getCompletedProjectDetails() {
    return api.get<ProjectDetailsReport[]>("/api/Reports/completed-project-details");
  },

  getOwnProjectBalances(startDate: string, endDate: string) {
    return api.get<OwnProjectBalance[]>("/api/Reports/own-project-balances", { startDate, endDate });
  },

  // Time Entries
  async getTimeEntries(params?: {
    startDate?: string;
    endDate?: string;
    projectId?: number;
  }) {
    return api.get<TimeEntry[]>("/api/TimeEntries", params);
  },

  async getTimeEntry(id: number) {
    return api.get<TimeEntry>(`/api/TimeEntries/${id}`);
  },

  async getWeekTimeEntries(weekStartDate: string) {
    return api.get<WeekTimeEntries>(`/api/TimeEntries/week/${weekStartDate}`);
  },

  async createTimeEntry(data: CreateTimeEntryRequest) {
    return api.post<TimeEntry>("/api/TimeEntries", data);
  },

  async bulkCreateTimeEntries(data: {
    projectId?: number | null;
    taskTypeId: number;
    entries: { date: string; hours: number }[];
    description?: string;
    costTypeId?: number | null;
  }) {
    return api.post<TimeEntry[]>("/api/TimeEntries/bulk", data);
  },

  async updateTimeEntry(id: number, data: UpdateTimeEntryRequest) {
    return api.put<TimeEntry>(`/api/TimeEntries/${id}`, data);
  },

  async deleteTimeEntry(id: number) {
    return api.delete<boolean>(`/api/TimeEntries/${id}`);
  },

  // Projects
  async getProjects(includeInactive = false) {
    return api.get<Project[]>("/api/Projects", { includeInactive });
  },

  async getProject(id: number) {
    return api.get<Project>(`/api/Projects/${id}`);
  },

  async createProject(data: CreateProjectRequest) {
    return api.post<Project>("/api/Projects", data);
  },

  async updateProject(id: number, data: UpdateProjectRequest) {
    return api.put<Project>(`/api/Projects/${id}`, data);
  },

  async deleteProject(id: number) {
    return api.delete<boolean>(`/api/Projects/${id}`);
  },

  // Task Types
  async getTaskTypes(includeInactive = false) {
    return api.get<TaskType[]>("/api/TaskTypes", { includeInactive });
  },

  async getTaskType(id: number) {
    return api.get<TaskType>(`/api/TaskTypes/${id}`);
  },

  async createTaskType(data: CreateTaskTypeRequest) {
    return api.post<TaskType>("/api/TaskTypes", data);
  },

  async updateTaskType(id: number, data: UpdateTaskTypeRequest) {
    return api.put<TaskType>(`/api/TaskTypes/${id}`, data);
  },

  async deleteTaskType(id: number) {
    return api.delete<boolean>(`/api/TaskTypes/${id}`);
  },

  // Clients
  async getClients(includeInactive = false) {
    return api.get<Client[]>("/api/Clients", { includeInactive });
  },

  async getClient(id: number) {
    return api.get<Client>(`/api/Clients/${id}`);
  },

  async createClient(data: CreateClientRequest) {
    return api.post<Client>("/api/Clients", data);
  },

  async updateClient(id: number, data: UpdateClientRequest) {
    return api.put<Client>(`/api/Clients/${id}`, data);
  },

  async deleteClient(id: number) {
    return api.delete<boolean>(`/api/Clients/${id}`);
  },

  // Project Roles
  async getProjectRoles(includeInactive = false) {
    return api.get<ProjectRole[]>("/api/ProjectRoles", { includeInactive });
  },

  async getProjectRolesByProject(projectId: number, includeInactive = false) {
    return api.get<ProjectRole[]>(`/api/ProjectRoles/project/${projectId}`, {
      includeInactive,
    });
  },

  async getProjectRolesByUser(userId: number, includeInactive = false) {
    return api.get<ProjectRole[]>(`/api/ProjectRoles/user/${userId}`, {
      includeInactive,
    });
  },

  // Project activities (task type ids configured on a project)
  async getProjectActivityIds(projectId: number) {
    return api.get<number[]>(`/api/Projects/${projectId}/activities`);
  },

  async getProjectRole(id: number) {
    return api.get<ProjectRole>(`/api/ProjectRoles/${id}`);
  },

  async createProjectRole(data: CreateProjectRoleRequest) {
    return api.post<ProjectRole>("/api/ProjectRoles", data);
  },

  async updateProjectRole(id: number, data: UpdateProjectRoleRequest) {
    return api.put<ProjectRole>(`/api/ProjectRoles/${id}`, data);
  },

  async deleteProjectRole(id: number) {
    return api.delete<boolean>(`/api/ProjectRoles/${id}`);
  },

  // Reports
  async getUserTimeReport(startDate: string, endDate: string) {
    return api.get<UserTimeReport>("/api/Reports/user", { startDate, endDate });
  },

  async getWeeklyReport(weekStartDate: string) {
    return api.get<WeeklyReport>("/api/Reports/weekly", { weekStartDate });
  },

  async exportTimeEntries(params: {
    startDate?: string;
    endDate?: string;
    projectId?: number;
    userId?: number;
    format: "excel" | "csv";
  }) {
    return api.downloadFile("/api/Reports/export", params);
  },

  // Cost Types
  async getCostTypes(includeInactive = false) {
    return api.get<CostType[]>("/api/CostTypes", { includeInactive });
  },

  async getCostType(id: number) {
    return api.get<CostType>(`/api/CostTypes/${id}`);
  },

  async setDefaultCostType(id: number) {
    return api.post<CostType>(`/api/CostTypes/${id}/set-default`, {});
  },

  // Project Members
  async getProjectMembers(params?: {
    projectId?: number;
    employeeId?: number;
    includeInactive?: boolean;
  }) {
    return api.get<any[]>("/api/ProjectMembers", params);
  },

  async getProjectTeam(projectId: number, includeInactive = false) {
    return api.get<any[]>(`/api/ProjectMembers/project/${projectId}`, {
      includeInactive,
    });
  },

  async getEmployeeProjects(employeeId: number, includeInactive = false) {
    return api.get<any[]>(`/api/ProjectMembers/employee/${employeeId}`, {
      includeInactive,
    });
  },
};
